import { InboundNotificationListItem } from '@shared/core/inbound-sms'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useObservable } from 'src/modules/core/hooks/use-observable'
import { GET_EVENT_LIST_CACHE_KEY } from 'src/modules/event/hooks/use-event-list'
import { TokenService } from 'src/services/token.service'
import { useInboundNotificationsPolling } from '../hooks/use-inbound-notifications-polling'

type InboundNotificationsContextValue = {
  notifications: InboundNotificationListItem[]
  unreadCount: number
  isOpen: boolean
  filterEventId: string | null
  openCenter: (eventId?: string) => void
  closeCenter: () => void
  clearFilter: () => void
  toggleCenter: () => void
  markRead: (id: string) => Promise<void>
  markUnread: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  toggleReadState: (id: string, isRead: boolean) => Promise<void>
  refreshNotifications: () => Promise<void>
  getPreviewForEvent: (eventId: string) => string | undefined
  handleEventBadgeOpen: (eventId: string) => void
}

const InboundNotificationsContext = createContext<InboundNotificationsContextValue | null>(null)

function buildPreviewByEventId(notifications: InboundNotificationListItem[]): Map<string, string> {
  const previewByEventId = new Map<string, string>()

  for (const notification of [...notifications].sort((left, right) =>
    right.created_at.localeCompare(left.created_at),
  )) {
    if (notification.read_at != null || !notification.event_id || !notification.message_preview) {
      continue
    }

    const eventId = String(notification.event_id)
    if (!previewByEventId.has(eventId)) {
      previewByEventId.set(eventId, notification.message_preview)
    }
  }

  return previewByEventId
}

export function InboundNotificationsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const token = useObservable(TokenService.getInstance().getToken())
  const [isOpen, setIsOpen] = useState(false)
  const [filterEventId, setFilterEventId] = useState<string | null>(null)
  // A failed refresh clears the token but leaves the dashboard mounted.
  const { notifications, unreadCount, markRead, markUnread, markAllRead, toggleReadState, refresh } =
    useInboundNotificationsPolling(Boolean(token))

  const previewByEventId = useMemo(() => buildPreviewByEventId(notifications), [notifications])

  const openCenter = useCallback((eventId?: string) => {
    setFilterEventId(eventId ?? null)
    setIsOpen(true)
  }, [])

  const closeCenter = useCallback(() => {
    setIsOpen(false)
    setFilterEventId(null)
  }, [])

  const clearFilter = useCallback(() => {
    setFilterEventId(null)
  }, [])

  const toggleCenter = useCallback(() => {
    setIsOpen((current) => {
      if (current) {
        setFilterEventId(null)
      }
      return !current
    })
  }, [])

  const getPreviewForEvent = useCallback((eventId: string) => previewByEventId.get(String(eventId)), [previewByEventId])

  const handleEventBadgeOpen = useCallback(
    (eventId: string) => {
      void refresh()
      void queryClient.invalidateQueries([GET_EVENT_LIST_CACHE_KEY])
      openCenter(eventId)
    },
    [openCenter, queryClient, refresh],
  )

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      isOpen,
      filterEventId,
      openCenter,
      closeCenter,
      clearFilter,
      toggleCenter,
      markRead,
      markUnread,
      markAllRead,
      toggleReadState,
      refreshNotifications: refresh,
      getPreviewForEvent,
      handleEventBadgeOpen,
    }),
    [
      notifications,
      unreadCount,
      isOpen,
      filterEventId,
      openCenter,
      closeCenter,
      clearFilter,
      toggleCenter,
      markRead,
      markUnread,
      markAllRead,
      toggleReadState,
      refresh,
      getPreviewForEvent,
      handleEventBadgeOpen,
    ],
  )

  return <InboundNotificationsContext.Provider value={value}>{children}</InboundNotificationsContext.Provider>
}

export function useInboundNotifications() {
  const context = useContext(InboundNotificationsContext)
  if (!context) {
    throw new Error('useInboundNotifications must be used within InboundNotificationsProvider')
  }
  return context
}
